import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Shuffle, ListOrdered, AlertCircle } from "lucide-react";
import api, { apiErrorMsg } from "@/lib/api";

export default function InputRankingPage() {
  const { periodeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ranks, setRanks] = useState({});

  useEffect(() => {
    api.get(`/pengasuh/periode/${periodeId}`).then((r) => {
      setData(r.data);
      setRanks(r.data.ranks || {});
    }).catch((e) => toast.error(apiErrorMsg(e)));
  }, [periodeId]);

  const setRank = (sid, value) => {
    const v = value === "" ? "" : Number(value);
    setRanks({ ...ranks, [sid]: v });
  };

  const acak = () => {
    if (!data) return;
    const ids = [...data.siswa_ids];
    const arr = Array.from({ length: ids.length }, (_, i) => i + 1);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    const next = {};
    ids.forEach((id, i) => next[id] = arr[i]);
    setRanks(next);
    toast.success("Ranking diacak");
  };

  const urut = () => {
    if (!data) return;
    const next = {};
    data.siswa_ids.forEach((id, i) => next[id] = i + 1);
    setRanks(next);
    toast.success("Ranking diurutkan 1-" + data.siswa_ids.length);
  };

  const simpan = async () => {
    if (!data) return;
    const n = data.siswa_ids.length;
    const allFilled = data.siswa_ids.every((id) => ranks[id] && ranks[id] >= 1 && ranks[id] <= n);
    if (!allFilled) return toast.error(`Semua siswa harus diberi ranking 1-${n}`);
    const values = data.siswa_ids.map((id) => ranks[id]);
    if (new Set(values).size !== n) return toast.error("Ranking tidak boleh ganda");
    try {
      await api.put(`/pengasuh/periode/${periodeId}/ranking`, { ranks });
      toast.success("Ranking tersimpan. Hasil observasi otomatis dihitung sistem.");
      navigate(`/pengasuh/periode/${periodeId}/hasil`);
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  if (!data) return <div className="p-4 md:p-8 text-slate-500">Memuat...</div>;

  const n = data.siswa_ids.length;
  const usedRanks = Object.values(ranks).filter((v) => v >= 1 && v <= n);
  const duplicates = new Set();
  const seen = new Set();
  usedRanks.forEach((v) => {
    if (seen.has(v)) duplicates.add(v);
    seen.add(v);
  });

  return (
    <div className="p-4 md:p-8">
      <Link to="/pengasuh" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 mb-4"><ArrowLeft className="w-4 h-4" /> Kembali</Link>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Atur Ranking</div>
        <h1 className="font-heading text-3xl font-bold text-navy-900">{data.jenis === "mingguan" ? "Minggu" : "Bulan"} ke-{data.nomor}</h1>
        <p className="text-slate-500 mt-1 text-sm">{data.bulan} {data.tahun} • {n} Serdik • Ranking 1 = Terbaik</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <strong>Petunjuk:</strong> Berikan ranking <span className="font-mono font-bold">1</span> hingga <span className="font-mono font-bold">{n}</span> untuk setiap Serdik. Sistem akan otomatis menghitung Hasil Observasi & Skor (4/5) per indikator berdasar rumus rangking.
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={acak} className="px-4 py-2 border border-slate-300 hover:border-brass-500 rounded-sm text-sm flex items-center gap-2"><Shuffle className="w-4 h-4" /> Acak</button>
        <button onClick={urut} className="px-4 py-2 border border-slate-300 hover:border-brass-500 rounded-sm text-sm flex items-center gap-2"><ListOrdered className="w-4 h-4" /> Urut</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto mb-4">
        <table className="w-full text-sm" data-testid="ranking-table">
          <thead>
            <tr className="bg-slate-100 text-slate-700 text-xs uppercase">
              <th className="p-3 w-12 text-left">#</th>
              <th className="p-3 text-left">Nama</th>
              <th className="p-3 w-32 text-left">NOSIS</th>
              <th className="p-3 w-32 text-center">Ranking</th>
            </tr>
          </thead>
          <tbody>
            {data.siswa.map((s, i) => {
              const v = ranks[s.id];
              const isDup = duplicates.has(v);
              return (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="p-3 text-slate-500 font-mono">{i + 1}</td>
                  <td className="p-3 font-medium text-navy-900">{s.nama}</td>
                  <td className="p-3 font-mono text-slate-600">{s.nosis}</td>
                  <td className="p-3 text-center">
                    <input type="number" min="1" max={n} value={v ?? ""} onChange={(e) => setRank(s.id, e.target.value)} data-testid={`rank-input-${s.id}`} className={`w-20 px-3 py-1.5 border-2 rounded-sm text-center font-mono font-bold ${isDup ? "border-red-400 bg-red-50" : "border-slate-300 focus:border-brass-500"} outline-none`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {duplicates.size > 0 && <div className="text-sm text-red-600 mb-3">Ada ranking ganda: {[...duplicates].join(", ")}</div>}

      <button onClick={simpan} data-testid="rank-save-btn" className="bg-brass-500 hover:bg-brass-600 text-white px-6 py-2.5 rounded-sm font-semibold text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Simpan & Hitung Hasil</button>
    </div>
  );
}
